import { useQuery, useMutation } from '@apollo/client/react/index.js';
import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassEmptyState,
  GlassInput,
} from '@merritrac/shared/components';
import { ArrowLeft, CheckCircle2, DoorOpen, MapPin, Save, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import { GET_UNIT, UPDATE_UNIT } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── Types ── */

interface ChecklistItem {
  key: string;
  label: string;
  value: string;
}

interface ApiUnit {
  accessKey: number;
  clientCode: string | null;
  linkedAk: number;
  unit: string | null;
  unitnumber: string | null;
  location: string | null;
  floor: string | null;
  ownerTenant: string | null;
  equipment: string | null;
  serviceDate: string | null;
  serviceComplete: number | null;
  accessDenied: number | null;
  lastUpdated: string | null;
  servicetech: string | null;
  comments: string | null;
  servicetime: number | null;
  checklistItems: string[];
  serviceOrder: {
    accessKey: number;
    ticketno: string | null;
    description: string | null;
    client: { clientName: string | null; clientCode: string | null } | null;
  } | null;
}

interface GetUnitData {
  serviceOrderUnit: ApiUnit | null;
}

/* ── Checklist item definitions ── */

function buildChecklistItems(unit: ApiUnit): ChecklistItem[] {
  return unit.checklistItems.map((value, i) => ({
    key: `Item ${String(i + 1)}`,
    label: `Item ${String(i + 1)}`,
    value,
  }));
}

/* ── Page ── */

export function UnitDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const accessKey = parseFloat(id ?? '0');

  const { data, loading, error } = useQuery<GetUnitData>(GET_UNIT, {
    variables: { accessKey },
    skip: isNaN(accessKey) || accessKey === 0,
  });

  const [updateUnit, { loading: saving }] = useMutation(UPDATE_UNIT, {
    refetchQueries: [{ query: GET_UNIT, variables: { accessKey } }],
  });

  const unit = data?.serviceOrderUnit ?? null;

  const [isEditing, setIsEditing] = useState(false);
  const [editFields, setEditFields] = useState({
    unit: '',
    unitnumber: '',
    location: '',
    floor: '',
    ownerTenant: '',
    equipment: '',
    serviceDate: '',
    serviceComplete: false,
    accessDenied: false,
    notes: '',
    items: [] as ChecklistItem[],
  });

  // Sync edit fields when data loads
  useEffect(() => {
    if (unit !== null && !isEditing) {
      setEditFields({
        unit: unit.unit ?? '',
        unitnumber: unit.unitnumber ?? '',
        location: unit.location ?? '',
        floor: unit.floor ?? '',
        ownerTenant: unit.ownerTenant ?? '',
        equipment: unit.equipment ?? '',
        serviceDate: unit.serviceDate ?? '',
        serviceComplete: unit.serviceComplete === 1,
        accessDenied: unit.accessDenied === 1,
        notes: '',
        items: buildChecklistItems(unit),
      });
    }
  }, [data]);

  function handleBack(): void {
    void navigate('/units');
  }

  function handleEditToggle(): void {
    if (unit !== null) {
      setEditFields({
        unit: unit.unit ?? '',
        unitnumber: unit.unitnumber ?? '',
        location: unit.location ?? '',
        floor: unit.floor ?? '',
        ownerTenant: unit.ownerTenant ?? '',
        equipment: unit.equipment ?? '',
        serviceDate: unit.serviceDate ?? '',
        serviceComplete: unit.serviceComplete === 1,
        accessDenied: unit.accessDenied === 1,
        notes: '',
        items: buildChecklistItems(unit),
      });
    }
    setIsEditing(true);
  }

  function handleCancel(): void {
    setIsEditing(false);
  }

  function handleSave(): void {
    void updateUnit({
      variables: {
        accessKey,
        unit: editFields.unit || undefined,
        unitnumber: editFields.unitnumber || undefined,
        location: editFields.location || undefined,
        floor: editFields.floor || undefined,
        ownerTenant: editFields.ownerTenant || undefined,
        equipment: editFields.equipment || undefined,
        serviceDate: editFields.serviceDate || undefined,
        serviceComplete: editFields.serviceComplete ? 1 : 0,
        accessDenied: editFields.accessDenied ? 1 : 0,
        notes: editFields.notes || undefined,
      },
    }).then(() => {
      setIsEditing(false);
    });
  }

  function handleFieldChange(field: keyof typeof editFields, value: unknown): void {
    setEditFields((prev) => ({ ...prev, [field]: value }));
  }

  function handleItemChange(index: number, value: string): void {
    setEditFields((prev) => {
      const items: ChecklistItem[] = [...prev.items];
      const item = items[index];
      if (item === undefined) {
        return prev;
      }
      items[index] = { key: item.key, label: item.label, value };
      return { ...prev, items };
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-text-secondary">
        Loading…
      </div>
    );
  }

  if (error !== undefined) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="text-sm text-danger">Failed to load unit: {error.message}</p>
        <GlassButton variant="secondary" size="md" onClick={handleBack}>
          <ArrowLeft size={14} />
          Back to Units
        </GlassButton>
      </div>
    );
  }

  if (unit === null) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <GlassEmptyState
          icon={MapPin}
          title="Unit not found"
          description={`No unit with ID "${id ?? ''}" could be located.`}
          action={
            <GlassButton variant="secondary" size="md" onClick={handleBack}>
              <ArrowLeft size={14} />
              Back to Units
            </GlassButton>
          }
        />
      </div>
    );
  }

  const isComplete = unit.serviceComplete === 1;
  const isAccessDenied = unit.accessDenied === 1;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={handleBack}
            aria-label="Back to Units"
            className="-ml-1 mt-0.5"
          >
            <ArrowLeft size={14} />
            Back
          </GlassButton>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <GlassButton variant="secondary" size="md" onClick={handleCancel} disabled={saving}>
                <X size={14} />
                Cancel
              </GlassButton>
              <GlassButton variant="primary" size="md" onClick={handleSave} disabled={saving}>
                <Save size={14} />
                {saving ? 'Saving…' : 'Save Changes'}
              </GlassButton>
            </>
          ) : (
            <GlassButton variant="secondary" size="md" onClick={handleEditToggle}>
              Edit Unit
            </GlassButton>
          )}
        </div>
      </div>

      {/* Unit header card */}
      <GlassCard>
        <GlassCardContent className="py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-semibold text-text-secondary">
                  #{String(unit.accessKey)}
                </span>
                {isComplete ? (
                  <GlassBadge variant="success">
                    <CheckCircle2 size={10} />
                    Complete
                  </GlassBadge>
                ) : (
                  <GlassBadge variant="warning">Incomplete</GlassBadge>
                )}
                {isAccessDenied ? (
                  <GlassBadge variant="danger">
                    <DoorOpen size={10} />
                    Access Denied
                  </GlassBadge>
                ) : null}
              </div>
              <h1 className="text-xl font-semibold text-text mt-1">
                {unit.unit ?? `Unit #${String(unit.accessKey)}`}
              </h1>
              {unit.serviceOrder && (
                <p className="text-sm text-text-secondary">
                  {unit.serviceOrder.ticketno ?? String(unit.serviceOrder.accessKey)} —{' '}
                  {unit.serviceOrder.client?.clientName ?? 'Unknown Client'}
                </p>
              )}
            </div>

            {/* Status toggles */}
            {isEditing ? (
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFields.serviceComplete}
                    onChange={(e): void => {
                      handleFieldChange('serviceComplete', e.target.checked);
                    }}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-text">Service Complete</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFields.accessDenied}
                    onChange={(e): void => {
                      handleFieldChange('accessDenied', e.target.checked);
                    }}
                    className="h-4 w-4 rounded border-border text-danger focus:ring-danger"
                  />
                  <span className="text-sm text-text">Access Denied</span>
                </label>
              </div>
            ) : (
              <div className="flex flex-col gap-1 sm:items-end text-sm text-text-secondary">
                {/* eslint-disable-next-line eqeqeq */}
                {unit.location != null && (
                  <div className="flex items-center gap-1.5">
                    <MapPin size={13} />
                    <span>{unit.location}</span>
                  </div>
                )}
                {/* eslint-disable-next-line eqeqeq */}
                {unit.floor != null && <span>Floor: {unit.floor}</span>}
                {/* eslint-disable-next-line eqeqeq */}
                {unit.lastUpdated != null && <span>Updated: {unit.lastUpdated}</span>}
              </div>
            )}
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Basic info */}
      <GlassCard>
        <GlassCardHeader>
          <h2 className="text-base font-semibold text-text">Unit Information</h2>
        </GlassCardHeader>
        <GlassCardContent>
          {isEditing ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <GlassInput
                label="Unit Name"
                value={editFields.unit}
                onChange={(e): void => {
                  handleFieldChange('unit', e.target.value);
                }}
              />
              <GlassInput
                label="Unit Number"
                value={editFields.unitnumber}
                onChange={(e): void => {
                  handleFieldChange('unitnumber', e.target.value);
                }}
              />
              <GlassInput
                label="Location"
                value={editFields.location}
                onChange={(e): void => {
                  handleFieldChange('location', e.target.value);
                }}
              />
              <GlassInput
                label="Floor"
                value={editFields.floor}
                onChange={(e): void => {
                  handleFieldChange('floor', e.target.value);
                }}
              />
              <GlassInput
                label="Owner/Tenant"
                value={editFields.ownerTenant}
                onChange={(e): void => {
                  handleFieldChange('ownerTenant', e.target.value);
                }}
              />
              <GlassInput
                label="Equipment"
                value={editFields.equipment}
                onChange={(e): void => {
                  handleFieldChange('equipment', e.target.value);
                }}
              />
              <GlassInput
                label="Service Date"
                type="date"
                value={editFields.serviceDate}
                onChange={(e): void => {
                  handleFieldChange('serviceDate', e.target.value);
                }}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Unit Name
                </p>
                <p className="text-sm text-text">{unit.unit ?? '—'}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Unit Number
                </p>
                <p className="text-sm text-text">{unit.unitnumber ?? '—'}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Location
                </p>
                <p className="text-sm text-text">{unit.location ?? '—'}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Floor
                </p>
                <p className="text-sm text-text">{unit.floor ?? '—'}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Owner/Tenant
                </p>
                <p className="text-sm text-text">{unit.ownerTenant ?? '—'}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Equipment
                </p>
                <p className="text-sm text-text">{unit.equipment ?? '—'}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Service Date
                </p>
                <p className="text-sm text-text">{unit.serviceDate ?? '—'}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Service Order
                </p>
                <p className="text-sm text-text">
                  {unit.serviceOrder?.ticketno ?? String(unit.linkedAk)}
                </p>
              </div>
            </div>
          )}
        </GlassCardContent>
      </GlassCard>

      {/* Checklist items */}
      {editFields.items.length > 0 && (
        <GlassCard>
          <GlassCardHeader>
            <h2 className="text-base font-semibold text-text">Checklist Data</h2>
          </GlassCardHeader>
          <GlassCardContent>
            {isEditing ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {editFields.items.map((item, i) => (
                  <GlassInput
                    key={item.key}
                    label={item.key}
                    value={item.value}
                    onChange={(e): void => {
                      handleItemChange(i, e.target.value);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {editFields.items.map((item) => (
                  <div key={item.key} className="flex flex-col gap-1">
                    <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                      {item.key}
                    </p>
                    <p className="text-sm text-text">{item.value}</p>
                  </div>
                ))}
              </div>
            )}
          </GlassCardContent>
        </GlassCard>
      )}

      {/* Service Order context */}
      {unit.serviceOrder && (
        <GlassCard>
          <GlassCardHeader>
            <h2 className="text-base font-semibold text-text">Service Order</h2>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <div className="flex-1 flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Order
                </p>
                <p className="text-sm text-text">
                  {unit.serviceOrder.ticketno ?? String(unit.serviceOrder.accessKey)}
                </p>
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Client
                </p>
                <p className="text-sm text-text">{unit.serviceOrder.client?.clientName ?? '—'}</p>
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                  Description
                </p>
                <p className="text-sm text-text">{unit.serviceOrder.description ?? '—'}</p>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      )}
    </div>
  );
}
